import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Alert, Avatar, Button, Col, DatePicker, Descriptions, Drawer, Dropdown, Empty, Form, Input, Modal, Popconfirm, Row, Select, Space, Spin, Table, Tabs, Tag, Timeline, Tooltip, Typography, Upload, message, InputNumber } from 'antd'
import { DeleteOutlined, EyeOutlined, MessageOutlined, PlusOutlined, ReloadOutlined, SaveOutlined, SearchOutlined, SwapOutlined, TeamOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons'
import type { UploadProps } from 'antd'
import dayjs from 'dayjs'
import { useNavigate, useParams } from 'react-router-dom'
import { leadsApi, type CrmDictionaries, type GeoFolder, type Lead, type LeadActivity, type LeadMethod, type LeadStatusDefinition, type AssigneeSummaryRow } from '../services/leadsApi'
import { operatorsApi } from '../services/api'
import { chatWorkspaceApi, type CloseReasonGroup } from '../services/chatWorkspaceApi'
import { crmIntegrationApi, type CrmCardPreview } from '../services/crmIntegrationApi'
import { DEFAULT_TEMPERATURE, TEMPERATURE_OPTIONS, temperatureColor, temperatureLabel, temperatureRank } from '../constants/leadTemperature'
import { useAuthStore } from '../store/authStore'
import { seesAllLeads } from '../constants/roles'
import { profileLabel, visibilityProfilesApi, type VisibilityProfile } from '../services/visibilityProfilesApi'
import LeadCard from '../components/LeadCard/LeadCard'
import LeadTasksTab from '../components/Tasks/LeadTasksTab'
import { CLOSED as CLOSED_TASK_STATUSES } from '../components/Tasks/taskShared'
import { tasksApi, type TaskItem } from '../services/tasksApi'

const { Text, Title } = Typography
const { TextArea } = Input

type FieldType = 'input' | 'textarea' | 'date' | 'select'

interface LeadFieldConfig {
    name: keyof Lead
    label: string
    type?: FieldType
    required?: boolean
    full?: boolean
}

const userPickFields = new Set<keyof Lead>(['onboarding_manager', 'onboarding_am', 'offboarding_am', 'manager'])
const hiddenActivityEvents = new Set(['message_sent', 'message_failed', 'message_pinned', 'message_unpinned', 'message_deleted', 'message_edited'])

const fieldLabels: Record<string, string> = {
    title: 'Название лида',
    source: 'Источник',
    source_details: 'Уточнение источника',
    geo: 'ГЕО',
    discovery_source: 'Откуда агент узнал о нас',
    admin_panel: 'Админка',
    legacy_conditions: 'Условия (Legacy)',
    intake_percent: 'Прием, %',
    payout_percent: 'Вывод, %',
    prepay: 'Препей',
    method: 'Метод',
    additional_info: 'Доп. информация',
    first_name: 'Имя',
    last_name: 'Фамилия',
    birth_date: 'Дата рождения',
    gender: 'Пол',
    telegram_nickname: 'TG Nickname',
    email: 'Email',
    phone: 'Телефон',
    crm_ww_id: 'ID CRM WW',
    temperature: 'Температура',
    company: 'Компания',
    position: 'Должность',
    link: 'Ссылка',
    lead_type: 'Тип',
    key_value: 'Ключевое значение',
    document_type: 'Тип документа',
    extra_contacts: 'Доп. контакты',
    onboarding_manager: 'Менеджер онбординга',
    onboarding_am: 'Ответственный AM за онбординг',
    offboarding_am: 'Ответственный AM за отключение',
    onboarded_at: 'Дата онборда',
    agent_type: 'Вид агента',
    document_1: 'Документ №1',
    document_2: 'Документ №2',
    kyc_id: 'ID KYC',
    kyc_link: 'Ссылка на KYC',
    chat_link: 'Ссылка на чат',
    agent_agreement_link: 'Agent agreement',
    drop_agreement_link: 'Drop agreement',
    jira_task_link: 'Ссылка на задачу в Jira',
    am_onboarding_checklist: 'Чек-лист AM (Онбординг)',
    finalization_checklist: 'Чек-лист Финализации',
    offboarding_checklist: 'Чек-лист отключения',
    status: 'Текущий статус',
    sub_status: 'Подстатус',
    refusal_reason: 'Причина отказа',
    manager: 'Менеджер',
    contact_email: 'Контактный e-mail',
    assigned_to_id: 'Ответственный',
    crm_methods: 'Методы и условия',
}

const fallbackStatusLabels: Record<string, string> = {
    new: 'Новый',
    primary_contact: 'Первичный контакт',
    negotiation: 'Сбор информации / Переговоры',
    onboarding: 'Онбординг',
    active_agent: 'Действующий агент',
    no_contact: 'Нет контакта',
    rejected: 'Отказ / Не подходит',
    closed: 'Отключен / Закрыт',
}

const activityEventLabels: Record<string, string> = {
    assigned: 'Назначен ответственный',
    imported: 'Лид импортирован',
    created: 'Лид создан',
    message_sent: 'Отправлено сообщение',
    message_failed: 'Ошибка отправки сообщения',
    account_changed: 'Сменен рабочий TG-аккаунт',
    message_pinned: 'Сообщение закреплено',
    message_unpinned: 'Сообщение откреплено',
    message_deleted: 'Сообщение удалено',
    message_edited: 'Сообщение отредактировано',
    blocked: 'Лид заблокирован',
    transferred: 'Лид передан вручную',
    crm_status_changed: 'Статус в CRM Wiseway изменён',
    crm_fields_changed: 'Карточка изменена в CRM Wiseway',
    auto_assigned: 'Лид распределён автоматически',
    dispatch_cancelled: 'Отправка первого сообщения отменена',
}

const sections: Array<{ title: string; fields: LeadFieldConfig[] }> = [
    {
        title: 'Основная информация',
        fields: [
            { name: 'title', label: fieldLabels.title, required: true },
            { name: 'telegram_nickname', label: fieldLabels.telegram_nickname, required: true },
            { name: 'geo', label: fieldLabels.geo, type: 'select', required: true },
            { name: 'temperature', label: fieldLabels.temperature, type: 'select', required: true },
            { name: 'phone', label: fieldLabels.phone },
            { name: 'crm_ww_id', label: fieldLabels.crm_ww_id },
            { name: 'source', label: fieldLabels.source },
            { name: 'source_details', label: fieldLabels.source_details },
            { name: 'lead_type', label: fieldLabels.lead_type, type: 'select' },
            { name: 'link', label: fieldLabels.link },
            { name: 'key_value', label: fieldLabels.key_value },
            { name: 'discovery_source', label: fieldLabels.discovery_source, type: 'select' },
            { name: 'admin_panel', label: fieldLabels.admin_panel },
            { name: 'legacy_conditions', label: fieldLabels.legacy_conditions },
            { name: 'prepay', label: fieldLabels.prepay },
            { name: 'additional_info', label: fieldLabels.additional_info, type: 'textarea', full: true },
        ],
    },
    {
        title: 'Контактная информация',
        fields: [
            { name: 'first_name', label: fieldLabels.first_name },
            { name: 'last_name', label: fieldLabels.last_name },
            { name: 'birth_date', label: fieldLabels.birth_date, type: 'date' },
            { name: 'gender', label: fieldLabels.gender, type: 'select' },
            { name: 'email', label: fieldLabels.email },
            { name: 'company', label: fieldLabels.company },
            { name: 'position', label: fieldLabels.position },
            { name: 'extra_contacts', label: fieldLabels.extra_contacts, type: 'textarea', full: true },
        ],
    },
    {
        title: 'Детали онбординга и ссылки',
        fields: [
            { name: 'onboarding_manager', label: fieldLabels.onboarding_manager },
            { name: 'onboarding_am', label: fieldLabels.onboarding_am },
            { name: 'offboarding_am', label: fieldLabels.offboarding_am },
            { name: 'onboarded_at', label: fieldLabels.onboarded_at, type: 'date' },
            { name: 'agent_type', label: fieldLabels.agent_type, type: 'select' },
            { name: 'document_type', label: fieldLabels.document_type },
            { name: 'document_1', label: fieldLabels.document_1, type: 'select' },
            { name: 'document_2', label: fieldLabels.document_2, type: 'select' },
            { name: 'kyc_id', label: fieldLabels.kyc_id },
            { name: 'kyc_link', label: fieldLabels.kyc_link },
            { name: 'chat_link', label: fieldLabels.chat_link },
            { name: 'agent_agreement_link', label: fieldLabels.agent_agreement_link },
            { name: 'drop_agreement_link', label: fieldLabels.drop_agreement_link },
            { name: 'jira_task_link', label: fieldLabels.jira_task_link },
            { name: 'am_onboarding_checklist', label: fieldLabels.am_onboarding_checklist, type: 'textarea', full: true },
            { name: 'finalization_checklist', label: fieldLabels.finalization_checklist, type: 'textarea', full: true },
        ],
    },
    {
        title: 'Процесс отключения',
        fields: [
            { name: 'offboarding_checklist', label: fieldLabels.offboarding_checklist, type: 'textarea', full: true },
        ],
    },
    {
        title: 'Ответственный',
        fields: [
            // СТАТУСНАЯ МОДЕЛЬ ОТКЛЮЧЕНА: поля статуса и подстатуса скрыты из карточки.
            // { name: 'status', label: fieldLabels.status, type: 'select' },
            // { name: 'sub_status', label: fieldLabels.sub_status, type: 'select' },
            { name: 'assigned_to_id', label: fieldLabels.assigned_to_id, type: 'select' },
            { name: 'refusal_reason', label: fieldLabels.refusal_reason, type: 'textarea', full: true },
            { name: 'manager', label: fieldLabels.manager },
            { name: 'contact_email', label: fieldLabels.contact_email },
        ],
    },
]

// Состояния работы по лиду. Те же вкладки, что в разделе «Чаты», - перенос
// отсюда должен давать ровно тот же результат, что кнопки в диалоге.
const WORK_STATES = [
    { key: 'replied', label: 'Ответили', state: 'none' },
    { key: 'active', label: 'Активные', state: 'active' },
    { key: 'postponed', label: 'Отложенные', state: 'postponed' },
    { key: 'closed', label: 'Закрытые', state: 'closed' },
] as const

type WorkAction = (typeof WORK_STATES)[number]['key']

// Поля, которые сервер вычищает из карточки для оператора. Показывать их
// пустыми смысла нет - в карточке они просто не появляются.
const HIDDEN_LEAD_FIELDS = ['telegram_nickname', 'phone', 'chat_link', 'extra_contacts']

// Значения приходят из CRM Wiseway: ID связывает наши карточки с их базой, а по
// источнику подбирается пул аккаунтов. Руками их не меняют - сервер такие
// правки тоже отбрасывает.
// Не правится руками: это связь нашей карточки с их карточкой.
const READONLY_LEAD_FIELDS = new Set<string>(['crm_ww_id'])

// Телеграм лида правится только при заведении карточки. Дальше он заперт: по
// нему привязан чат и по нему же сверяется карточка в CRM Wiseway, так что
// опечатка здесь рвёт сразу две связи.
const LOCKED_AFTER_CREATE = new Set<string>(['telegram_nickname'])

// Запасной список связанных с CRM полей - на случай, если справочник с сервера
// ещё не приехал. Основной источник - backend/app/constants/crm_lead_fields.py.
// Значения, которые в карточке CRM Wiseway выбираются из списка. Списки взяты
// из их интерфейса; в базе это обычный текст, но значение вне списка не попадёт
// в их отчёты, поэтому вводить руками такие поля нельзя.
const CRM_OPTIONS: Record<string, string[]> = {
    gender: ['Мужской', 'Женский'],
    lead_type: ['агент', 'дроп'],
    agent_type: ['Обычный', 'Корпорэйт', 'С АПИ', 'current', 'fonepay'],
    document_1: ['Паспорт', 'ID', 'Загран паспорт', 'Водительские права', 'Свидетельство о рождении', 'Adhaar', 'PanCard'],
    document_2: ['Паспорт', 'ID', 'Загран паспорт', 'Водительские права', 'Свидетельство о рождении', 'Adhaar', 'PanCard'],
    discovery_source: [
        'Linkedin', 'Личные группы ЛПЕ', 'От Друга/Знакомого', 'Реклама ФБ', 'Реклама Ютуб',
        'Реклама в Инстаграм', 'Реклама в Телеграм', 'Реклама в локальных соц. сетях',
        'Социальные сети конкурентов', 'Холодный поиск ТГ', 'Через поиск (Сайт)',
    ],
}

const CRM_SYNCED_FIELDS = new Set<string>([
    'title', 'first_name', 'last_name', 'email', 'phone', 'geo', 'additional_info',
    'source_details', 'company', 'extra_contacts', 'lead_type', 'agent_type', 'gender',
    'kyc_id', 'kyc_link', 'chat_link', 'agent_agreement_link', 'drop_agreement_link',
    'jira_task_link', 'refusal_reason', 'legacy_conditions', 'prepay', 'admin_panel',
    'discovery_source', 'document_1', 'document_2', 'birth_date', 'onboarded_at',
])

// Сортировка текста в таблице лидов: без учёта регистра, числа внутри строк
// по значению («Лид 2» раньше «Лид 10»). Пустые значения всегда уходят вниз
// при сортировке по возрастанию.
// Сколько лидов на странице таблицы - запоминаем в браузере, как в «TG аккаунтах».
const LEADS_PAGE_SIZE_KEY = 'orchestrator.leadsTable.pageSize'
const LEADS_PAGE_SIZES = [10, 20, 50, 100]

function loadLeadsPageSize(): number {
    try {
        const saved = Number(localStorage.getItem(LEADS_PAGE_SIZE_KEY))
        return LEADS_PAGE_SIZES.includes(saved) ? saved : 20
    } catch {
        return 20
    }
}

const textCollator = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' })

function compareText(left: string | null | undefined, right: string | null | undefined): number {
    const a = (left || '').trim()
    const b = (right || '').trim()
    if (!a || !b) return (a ? 0 : 1) - (b ? 0 : 1)
    return textCollator.compare(a, b)
}

function leadName(lead: Lead): string {
    return lead.title || [lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.telegram_nickname || leadRef(lead)
}

// Чем лид называется, когда ник и телефон скрыты: идентификатор его карточки
// в CRM Wiseway, а у заведённых вручную - наш номер.
function leadRef(lead: Pick<Lead, 'id' | 'crm_ww_id'>): string {
    const crmId = (lead.crm_ww_id || '').trim()
    return crmId ? `ID CRM WW ${crmId}` : `Лид #${lead.id}`
}

function leadContact(lead: Lead): string {
    // Со скрытыми контактами вместо способа связи показываем идентификатор
    // карточки: он и служит оператору именем лида.
    if (lead.contacts_hidden) return leadRef(lead)
    const value = lead.telegram_username || lead.telegram_nickname || lead.phone || lead.email || ''
    if (!value) return '-'
    return value.startsWith('@') || value.startsWith('+') ? value : lead.telegram_username ? `@${value}` : value
}

function extractError(error: unknown): string {
    if (typeof error === 'object' && error && 'response' in error) {
        const response = (error as { response?: { data?: { detail?: string } } }).response
        return response?.data?.detail || 'Ошибка запроса'
    }
    return 'Ошибка запроса'
}

function activityValue(fieldName: string | null | undefined, value: string | null | undefined, statusLabelByKey: Record<string, string>): string {
    if (!value) return '-'
    if (fieldName === 'status') return statusLabelByKey[value] || fallbackStatusLabels[value] || value
    return value
}

function localizeActivityDescription(description: string, statusLabelByKey: Record<string, string>): string {
    let result = description
    for (const [key, label] of Object.entries({ ...fallbackStatusLabels, ...statusLabelByKey })) {
        result = result.replace(new RegExp(`\\b${key}\\b`, 'g'), label)
    }
    for (const [key, label] of Object.entries(fieldLabels)) {
        result = result.replace(new RegExp(`\\b${key}\\b`, 'g'), label)
    }
    return result
}

function activityText(activity: LeadActivity, statusLabelByKey: Record<string, string>): string {
    if (activity.description) return localizeActivityDescription(activity.description, statusLabelByKey)
    if (activity.event_type === 'field_changed' && activity.field_name) {
        const label = fieldLabels[activity.field_name] || activity.field_name
        return `${label}: ${activityValue(activity.field_name, activity.old_value, statusLabelByKey)} → ${activityValue(activity.field_name, activity.new_value, statusLabelByKey)}`
    }
    if (activity.event_type === 'status_changed' && activity.field_name) {
        const label = fieldLabels[activity.field_name] || activity.field_name
        return `${label}: ${activityValue(activity.field_name, activity.old_value, statusLabelByKey)} → ${activityValue(activity.field_name, activity.new_value, statusLabelByKey)}`
    }
    return activityEventLabels[activity.event_type] || 'Событие обновлено'
}

function normalizePayload(values: Record<string, unknown>) {
    const payload: Record<string, unknown> = { ...values }
    for (const field of ['birth_date', 'onboarded_at']) {
        const value = payload[field]
        if (dayjs.isDayjs(value)) {
            payload[field] = value.format('YYYY-MM-DD')
        } else if (!value) {
            payload[field] = null
        }
    }
    userPickFields.forEach((field) => {
        const value = payload[field]
        if (Array.isArray(value)) payload[field] = value[0] || null
    })
    Object.keys(payload).forEach((key) => {
        if (payload[key] === '') payload[key] = null
    })
    return payload
}

interface ContactsProps {
    // Встраиваемый режим: страница показывает только окно карточки указанного
    // лида. Нужен разделу «Чаты», чтобы «Открыть» выезжало тем же окном, а не
    // уводило со страницы. Одна реализация карточки на оба места.
    embeddedLeadId?: number | null
    onEmbeddedClose?: () => void
    embeddedWidth?: number
}

export default function Contacts({ embeddedLeadId = null, onEmbeddedClose, embeddedWidth = 560 }: ContactsProps = {}) {
    const navigate = useNavigate()
    const { leadId: routeLeadId } = useParams()
    const embedded = embeddedLeadId != null
    const leadId = embedded ? String(embeddedLeadId) : routeLeadId
    const [form] = Form.useForm()
    const { user } = useAuthStore()
    const selectedStatus = Form.useWatch('status', form)
    const [leads, setLeads] = useState<Lead[]>([])
    const [geos, setGeos] = useState<GeoFolder[]>([])
    // Списки значений карточки CRM: приходят с сервера, там же и их источник.
    const [crmDict, setCrmDict] = useState<CrmDictionaries | null>(null)
    const [statuses, setStatuses] = useState<LeadStatusDefinition[]>([])
    const [operators, setOperators] = useState<Array<{ id: number; username: string; full_name?: string | null; department?: string | null }>>([])
    const [activities, setActivities] = useState<LeadActivity[]>([])
    const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
    // Задачи открытого лида - для вкладки «Задачи» и счётчика на ней.
    const [leadTasks, setLeadTasks] = useState<TaskItem[]>([])
    const [leadTasksLoading, setLeadTasksLoading] = useState(false)
    // Профили видимости - команды, в которые кладут и передают лидов.
    const [profiles, setProfiles] = useState<VisibilityProfile[]>([])

    useEffect(() => {
        visibilityProfilesApi.list()
            .then(({ data }) => setProfiles(data))
            .catch(() => setProfiles([]))
    }, [])
    const [loading, setLoading] = useState(false)
    const [leadsPageSize, setLeadsPageSize] = useState<number>(loadLeadsPageSize)
    const [detailsLoading, setDetailsLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [createMode, setCreateMode] = useState(false)
    const [editMode, setEditMode] = useState(false)
    const [search, setSearch] = useState('')
    const [importOpen, setImportOpen] = useState(false)
    const [importFile, setImportFile] = useState<File | null>(null)
    const [importing, setImporting] = useState(false)

    // Массовая передача лидов. Отметить можно как отдельные строки, так и все
    // сразу - и отправить их одному сотруднику одним действием.
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const [transferOpen, setTransferOpen] = useState(false)
    const [transferTo, setTransferTo] = useState<number | null | undefined>(undefined)
    const [transferComment, setTransferComment] = useState('')
    const [transferring, setTransferring] = useState(false)

    // Массовая смена статуса - тем же набором отметок, что и передача.
    const [bulkStatusOpen, setBulkStatusOpen] = useState(false)
    const [bulkStatusValue, setBulkStatusValue] = useState<string | undefined>(undefined)
    const [bulkStatusGroups, setBulkStatusGroups] = useState<CloseReasonGroup[]>([])
    const [bulkStatusLoading, setBulkStatusLoading] = useState(false)
    const [bulkStatusSaving, setBulkStatusSaving] = useState(false)
    // ALL_ASSIGNEES - фильтр выключен, NO_ASSIGNEE - лиды без ответственного.
    // 'all' | 'none' | 'p:<профиль>' | 'o:<сотрудник>'
    const [assigneeFilter, setAssigneeFilter] = useState<string>('all')
    // Фильтр по статусу лида в CRM. 'none' - статус ещё не прочитан.
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [summary, setSummary] = useState<AssigneeSummaryRow[]>([])
    const [summaryOpen, setSummaryOpen] = useState(false)
    const currentStatus = useMemo(
        () => statuses.find((status) => status.key === selectedStatus) || statuses[0],
        [selectedStatus, statuses]
    )

    const statusMap = useMemo(
        () => new Map(statuses.map((status) => [status.key, status])),
        [statuses]
    )

    // Управление лидом по правой кнопке: доступно тимлиду и суперадмину.
    const isManager = seesAllLeads(user?.role)
    const [menuLead, setMenuLead] = useState<Lead | null>(null)
    const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null)
    const [assignOpen, setAssignOpen] = useState(false)
    const [assignValue, setAssignValue] = useState<number | undefined>(undefined)
    const [moveAction, setMoveAction] = useState<WorkAction | null>(null)
    const [moveOutcome, setMoveOutcome] = useState<string | undefined>(undefined)
    const [moveComment, setMoveComment] = useState('')
    const [closeGroups, setCloseGroups] = useState<CloseReasonGroup[]>([])
    const [reopenGroups, setReopenGroups] = useState<CloseReasonGroup[]>([])
    const [workflowSaving, setWorkflowSaving] = useState(false)

    useEffect(() => {
        if (!isManager) return
        chatWorkspaceApi.getOutcomes().then((response) => setCloseGroups(response.data.groups)).catch(() => undefined)
        chatWorkspaceApi.getReopenOutcomes().then((response) => setReopenGroups(response.data.groups)).catch(() => undefined)
    }, [isManager])

    // Возврат закрытого лида в работу - движение по воронке Wiseway, поэтому
    // причина обязательна так же, как при закрытии.
    const needsReopenReason = Boolean(menuLead && menuLead.work_state === 'closed' && (moveAction === 'active' || moveAction === 'replied'))
    const needsCloseReason = moveAction === 'closed'
    const needsComment = moveAction === 'postponed'

    const closeWorkflowModal = () => {
        setMoveAction(null)
        setMoveOutcome(undefined)
        setMoveComment('')
    }

    const runWorkflow = async (data: Parameters<typeof chatWorkspaceApi.leadWorkflow>[1], successText: string) => {
        if (!menuLead) return
        setWorkflowSaving(true)
        try {
            const response = await chatWorkspaceApi.leadWorkflow(menuLead.id, data)
            message.success(successText)
            if (response.data.crm_warning) message.warning(response.data.crm_warning)
            setAssignOpen(false)
            closeWorkflowModal()
            setMenuLead(null)
            await loadLeads()
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setWorkflowSaving(false)
        }
    }

    const submitAssign = () => {
        if (!assignValue) {
            message.warning('Выберите сотрудника')
            return
        }
        runWorkflow({ action: 'assign', assigned_to_id: assignValue }, 'Ответственный назначен')
    }

    const submitMove = () => {
        if (!moveAction) return
        if ((needsCloseReason || needsReopenReason) && !moveOutcome) {
            message.warning(needsCloseReason ? 'Выберите причину закрытия' : 'Выберите статус, в который вернуть лида')
            return
        }
        if (needsComment && !moveComment.trim()) {
            message.warning('Добавьте комментарий')
            return
        }
        const label = WORK_STATES.find((item) => item.key === moveAction)?.label
        runWorkflow(
            { action: moveAction, outcome: moveOutcome, comment: moveComment.trim() || undefined },
            `Лид перенесён в «${label}»`
        )
    }

    const startMove = (lead: Lead, action: WorkAction) => {
        setMenuLead(lead)
        setMoveOutcome(undefined)
        setMoveComment('')
        const closed = lead.work_state === 'closed'
        // Без причины и комментария переносить нечего спрашивать - выполняем сразу.
        if (action === 'closed' || action === 'postponed' || (closed && (action === 'active' || action === 'replied'))) {
            setMoveAction(action)
            return
        }
        setMoveAction(null)
        setWorkflowSaving(true)
        chatWorkspaceApi.leadWorkflow(lead.id, { action })
            .then(async (response) => {
                const label = WORK_STATES.find((item) => item.key === action)?.label
                message.success(`Лид перенесён в «${label}»`)
                if (response.data.crm_warning) message.warning(response.data.crm_warning)
                setMenuLead(null)
                await loadLeads()
            })
            .catch((error) => message.error(extractError(error)))
            .finally(() => setWorkflowSaving(false))
    }

    // При создании лида контакт вводит сам оператор, поэтому там поля остаются:
    // скрывать нужно чужие данные, а не то, что он вносит своими руками.
    const visibleSections = useMemo(() => {
        if (createMode || !selectedLead?.contacts_hidden) return sections
        return sections
            .map((section) => ({
                ...section,
                fields: section.fields.filter((field) => !HIDDEN_LEAD_FIELDS.includes(field.name)),
            }))
            .filter((section) => section.fields.length > 0)
    }, [createMode, selectedLead])
    const statusLabelByKey = useMemo(
        () => Object.fromEntries(statuses.map((status) => [status.key, status.label])),
        [statuses]
    )
    const visibleActivities = useMemo(
        () => activities.filter((activity) => !hiddenActivityEvents.has(activity.event_type)),
        [activities]
    )

    const applyLeadToForm = useCallback((lead: Lead) => {
        const leadValues: Record<string, unknown> = { ...lead }
        userPickFields.forEach((field) => {
            const value = leadValues[field]
            leadValues[field] = value ? [value] : undefined
        })
        form.setFieldsValue({
            ...leadValues,
            birth_date: lead.birth_date ? dayjs(lead.birth_date) : null,
            onboarded_at: lead.onboarded_at ? dayjs(lead.onboarded_at) : null,
        })
    }, [form])

    const loadDictionaries = useCallback(async () => {
        try {
            const [geoResponse, statusResponse, operatorResponse, dictResponse] = await Promise.all([
                leadsApi.getGeos(), leadsApi.getStatuses(), operatorsApi.getAll(0, 500), leadsApi.getCrmDictionaries(),
            ])
            setGeos(geoResponse.data)
            setStatuses(statusResponse.data)
            setOperators(operatorResponse.data)
            setCrmDict(dictResponse.data)
        } catch (error) {
            message.error(extractError(error))
        }
    }, [])

    const loadLeads = useCallback(async () => {
        // Встроенное окно показывает одну карточку: таблица лидов там не
        // рисуется, и тянуть весь список незачем.
        if (embedded) return
        setLoading(true)
        try {
            const response = await leadsApi.getLeads({ scope: 'all', search: search || undefined, limit: 500 })
            setLeads(response.data)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoading(false)
        }
    }, [search, embedded])

    // Сводка по сотрудникам: сколько лидов на ком висит. Только руководителям -
    // сервер отдаёт её тем же ролям.
    const loadSummary = useCallback(async () => {
        if (embedded || !isManager) return
        try {
            const { data } = await leadsApi.getAssigneeSummary()
            setSummary(data)
        } catch {
            // Сводка - вспомогательная панель, без неё раздел работает.
        }
    }, [embedded, isManager])

    useEffect(() => {
        loadSummary()
    }, [loadSummary])

    // Статусы для выпадающего списка: только те, что реально встречаются, и
    // сразу со счётчиком. Справочник CRM большой, и пустые пункты в нём мешали бы.
    const statusOptions = useMemo(() => {
        const counts = new Map<string, { label: string; count: number }>()
        let withoutStatus = 0
        for (const lead of leads) {
            if (!lead.crm_status) {
                withoutStatus += 1
                continue
            }
            const entry = counts.get(lead.crm_status)
            if (entry) entry.count += 1
            else counts.set(lead.crm_status, { label: lead.crm_status_label || lead.crm_status, count: 1 })
        }
        const options = [...counts.entries()]
            .sort((left, right) => left[1].label.localeCompare(right[1].label))
            .map(([code, item]) => ({ value: code, label: `${item.label} (${item.count})` }))
        if (withoutStatus) {
            options.push({ value: 'none', label: `Статус не прочитан (${withoutStatus})` })
        }
        return [{ value: 'all', label: `Все статусы (${leads.length})` }, ...options]
    }, [leads])

    // Фильтры применяем на загруженном списке: он приходит одной пачкой, и
    // лишний запрос к серверу тут ничего не ускорит.
    const visibleLeads = useMemo(() => {
        let result = leads
        // Ответственный - профиль (команда), а без профиля - сотрудник.
        if (assigneeFilter === 'none') {
            result = result.filter((lead) => !lead.visibility_profile_id && !lead.assigned_to_id)
        } else if (assigneeFilter.startsWith('p:')) {
            const profileId = Number(assigneeFilter.slice(2))
            result = result.filter((lead) => lead.visibility_profile_id === profileId)
        } else if (assigneeFilter.startsWith('o:')) {
            const operatorId = Number(assigneeFilter.slice(2))
            result = result.filter((lead) => lead.assigned_to_id === operatorId)
        }

        if (statusFilter === 'none') result = result.filter((lead) => !lead.crm_status)
        else if (statusFilter !== 'all') result = result.filter((lead) => lead.crm_status === statusFilter)

        return result
    }, [leads, assigneeFilter, statusFilter])

    // Отметки со скрытых фильтром строк снимаем: иначе передалось бы больше,
    // чем видно на экране.
    useEffect(() => {
        setSelectedIds((current) => {
            const visible = new Set(visibleLeads.map((lead) => lead.id))
            const kept = current.filter((id) => visible.has(id))
            return kept.length === current.length ? current : kept
        })
    }, [visibleLeads])

    // Менять статусы пачкой может любой, кому доступен хоть один статус, - в
    // том числе ЛМ по своим лидам. Передача лидов осталась за руководителями.
    const canBulkStatus = (user?.allowed_statuses?.length ?? 0) > 0

    const openBulkStatus = async () => {
        setBulkStatusOpen(true)
        setBulkStatusValue(undefined)
        setBulkStatusLoading(true)
        try {
            // Список статусов сервер отдаёт уже отфильтрованным по роли.
            const { data } = await leadsApi.getCrmStatusOptions(selectedIds[0])
            setBulkStatusGroups(data.groups)
        } catch (error) {
            message.error(extractError(error))
            setBulkStatusOpen(false)
        } finally {
            setBulkStatusLoading(false)
        }
    }

    const submitBulkStatus = async () => {
        if (!bulkStatusValue) {
            message.warning('Выберите статус')
            return
        }
        setBulkStatusSaving(true)
        try {
            const { data } = await leadsApi.bulkSetCrmStatus(selectedIds, bulkStatusValue)
            const parts = [`Статус «${data.status_label}» проставлен: ${data.changed}`]
            if (data.skipped) parts.push(`пропущено: ${data.skipped}`)
            if (data.failed) parts.push(`не удалось: ${data.failed}`)
            if (data.skipped || data.failed) message.warning(parts.join(', '))
            else message.success(parts.join(', '))
            data.warnings.forEach((warning) => message.warning(warning))
            setBulkStatusOpen(false)
            setSelectedIds([])
            await Promise.all([loadLeads(), loadSummary()])
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setBulkStatusSaving(false)
        }
    }

    const openTransfer = () => {
        setTransferTo(undefined)
        setTransferComment('')
        setTransferOpen(true)
    }

    const submitTransfer = async () => {
        if (transferTo === undefined) {
            message.warning('Выберите, на кого передать лидов')
            return
        }
        setTransferring(true)
        try {
            const { data } = await leadsApi.transferLeads(selectedIds, transferTo, transferComment)
            const target = data.assigned_to_name || 'общий пул'
            message.success(
                `Передано лидов: ${data.moved} → ${target}`
                + (data.skipped ? `. Уже были там: ${data.skipped}` : '')
            )
            setTransferOpen(false)
            setSelectedIds([])
            await Promise.all([loadLeads(), loadSummary()])
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setTransferring(false)
        }
    }

    const loadLeadDetails = useCallback(async (id: number) => {
        setDetailsLoading(true)
        try {
            // История чужой карточки может быть недоступна роли - тогда
            // карточка открывается без неё, а не закрывается целиком.
            const [leadResponse, activityResponse] = await Promise.all([
                leadsApi.getLead(id),
                leadsApi.getActivities(id).catch(() => null),
            ])
            setSelectedLead(leadResponse.data)
            setActivities(activityResponse?.data || [])
            setEditMode(false)
            applyLeadToForm(leadResponse.data)
        } catch (error) {
            message.error(extractError(error))
            if (embedded) onEmbeddedClose?.()
            else navigate('/contacts')
        } finally {
            setDetailsLoading(false)
        }
    }, [applyLeadToForm, navigate])

    const loadLeadTasks = useCallback(async (id: number) => {
        setLeadTasksLoading(true)
        try {
            const { data } = await tasksApi.getTasks({ lead_id: id })
            setLeadTasks(data)
        } catch {
            setLeadTasks([])
        } finally {
            setLeadTasksLoading(false)
        }
    }, [])

    const openedLeadId = !createMode ? selectedLead?.id : undefined
    useEffect(() => {
        if (openedLeadId) void loadLeadTasks(openedLeadId)
        else setLeadTasks([])
    }, [openedLeadId, loadLeadTasks])

    const openTaskCount = leadTasks.filter((task) => !CLOSED_TASK_STATUSES.includes(task.status)).length
    const overdueTaskCount = leadTasks.filter((task) =>
        !CLOSED_TASK_STATUSES.includes(task.status) && task.deadline && dayjs(task.deadline).isBefore(dayjs())
    ).length

    useEffect(() => {
        loadDictionaries()
    }, [loadDictionaries])

    useEffect(() => {
        loadLeads()
    }, [loadLeads])

    useEffect(() => {
        if (leadId) {
            setCreateMode(false)
            loadLeadDetails(Number(leadId))
        } else if (!createMode) {
            setSelectedLead(null)
            setActivities([])
            form.resetFields()
        }
    }, [leadId, createMode, loadLeadDetails, form])

    useEffect(() => {
        if (!leadId || createMode || editMode) return
        const id = Number(leadId)
        if (!id) return
        const timer = window.setInterval(async () => {
            try {
                const response = await leadsApi.getLead(id)
                setSelectedLead((current) => current?.id === id ? response.data : current)
                setLeads((items) => items.map((item) => item.id === id ? response.data : item))
                applyLeadToForm(response.data)
            } catch {
            }
        }, 5000)
        return () => window.clearInterval(timer)
    }, [leadId, createMode, editMode, applyLeadToForm])

    // Добавление по ID карточки CRM Wiseway: сначала поиск карточки, потом
    // решение человека. Вводится только номер, остальное читается из их базы.
    const [crmIdOpen, setCrmIdOpen] = useState(false)
    const [crmIdValue, setCrmIdValue] = useState('')
    const [crmCard, setCrmCard] = useState<CrmCardPreview | null>(null)
    const [crmSearching, setCrmSearching] = useState(false)
    const [crmSaving, setCrmSaving] = useState(false)
    const [crmError, setCrmError] = useState<string | null>(null)

    const openCrmIdSearch = () => {
        setCrmIdValue('')
        setCrmCard(null)
        setCrmError(null)
        setCrmIdOpen(true)
    }

    const searchCrmCard = async () => {
        const value = crmIdValue.trim()
        if (!value) return
        setCrmSearching(true)
        setCrmCard(null)
        setCrmError(null)
        try {
            const { data } = await crmIntegrationApi.previewCrmCard(value)
            setCrmCard(data)
        } catch (error) {
            setCrmError(extractError(error))
        } finally {
            setCrmSearching(false)
        }
    }

    const confirmCrmCard = async () => {
        if (!crmCard?.can_add) return
        setCrmSaving(true)
        try {
            // Дубль уже показан в окне: если человек всё равно нажал «Добавить»,
            // это его решение, и повторная проверка на сервере не нужна.
            const { data } = await crmIntegrationApi.addLeadByCrmId(crmCard.crm_ww_id, Boolean(crmCard.duplicate))
            message.success(`Лид «${data.title}» добавлен из CRM Wiseway`)
            setCrmIdOpen(false)
            await loadLeads()
        } catch (error) {
            setCrmError(extractError(error))
        } finally {
            setCrmSaving(false)
        }
    }

    const openCreate = () => {
        setCreateMode(true)
        setEditMode(true)
        setSelectedLead(null)
        setActivities([])
        form.resetFields()
        const firstStatus = statuses[0]
        form.setFieldsValue({
            status: firstStatus?.key || 'new',
            sub_status: firstStatus?.substatuses?.[0] || null,
            geo: geos[0]?.name,
            temperature: DEFAULT_TEMPERATURE,
        })
    }

    const closeDrawer = () => {
        setCreateMode(false)
        setEditMode(false)
        if (embedded) {
            onEmbeddedClose?.()
            return
        }
        navigate('/contacts')
    }

    const handleSave = async () => {
        try {
            const values = await form.validateFields()
            setSaving(true)
            const payload = normalizePayload(values)
            if (createMode) {
                const response = await leadsApi.create(payload as Partial<Lead>)
                message.success('Лид создан')
                setCreateMode(false)
                await loadLeads()
                navigate(`/contacts/${response.data.id}`)
            } else if (selectedLead) {
                const response = await leadsApi.update(selectedLead.id, payload as Partial<Lead>)
                setSelectedLead(response.data)
                setEditMode(false)
                message.success('Лид обновлен')
                // Карточка у нас сохранена в любом случае: если правка не ушла
                // в CRM Wiseway, об этом говорим отдельно и не молча.
                if (response.data.crm_warning) message.warning(response.data.crm_warning, 6)
                await Promise.all([loadLeads(), loadLeadDetails(response.data.id)])
                // Во встроенной карточке остаёмся в правке: её заполняют подряд,
                // копируя данные из переписки, и выходить из формы после каждого
                // поля не нужно.
                if (embedded) setEditMode(true)
            }
        } catch (error) {
            if (typeof error === 'object' && error && 'errorFields' in error) return
            message.error(extractError(error))
        } finally {
            setSaving(false)
        }
    }

    const handleImport = async () => {
        if (!importFile) {
            message.warning('Выберите файл с лидами')
            return
        }
        setImporting(true)
        try {
            const response = await leadsApi.importFile(importFile)
            message.success(`Импортировано: ${response.data.created}, ошибок: ${response.data.failed}`)
            if (response.data.errors.length) {
                Modal.warning({
                    title: 'Ошибки импорта',
                    content: <div style={{ maxHeight: 300, overflowY: 'auto' }}>{response.data.errors.map((item) => <div key={item}>{item}</div>)}</div>,
                })
            }
            setImportOpen(false)
            setImportFile(null)
            await loadLeads()
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setImporting(false)
        }
    }

    const handleDeleteLead = async (lead: Lead) => {
        try {
            await leadsApi.delete(lead.id)
            message.success('Лид и чат удалены')
            if (selectedLead?.id === lead.id) {
                setSelectedLead(null)
                setActivities([])
                setCreateMode(false)
                setEditMode(false)
                form.resetFields()
                navigate('/contacts')
            }
            await loadLeads()
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const openLeadChat = (lead: Lead) => {
        if (!lead.chat_id) return
        const params = new URLSearchParams()
        if (lead.geo) params.set('geo', lead.geo)
        params.set('leadId', String(lead.id))
        navigate(`/chats?${params.toString()}`)
    }

    const uploadProps: UploadProps = {
        maxCount: 1,
        beforeUpload: (file) => {
            setImportFile(file as File)
            return false
        },
        onRemove: () => {
            setImportFile(null)
        },
        accept: '.xlsx,.xlsm,.xls,.csv,.tsv,.txt',
    }

    // Методы и условия работы: у лида их может быть несколько - как в карточке
    // CRM Wiseway. Варианты метода зависят от гео и берутся из их справочника.
    const renderMethodsEditor = () => (
        // Список методов перерисовывается при смене гео: варианты у каждого
        // гео свои, ровно как в их карточке.
        <Form.Item noStyle shouldUpdate={(prev, next) => prev.geo !== next.geo}>
            {() => {
        const geoValue = form.getFieldValue('geo') || selectedLead?.geo || ''
        const allowed = crmDict?.geo_methods?.[geoValue] || crmDict?.method_fallback || ['Ручник', 'AUTO', 'API']
        return (
            <div style={{ marginBottom: 22 }}>
                <Title level={5} style={{ marginBottom: 14 }}>Методы и условия</Title>
                <Form.List name="crm_methods">
                    {(rows, { add, remove }) => (
                        <>
                            {rows.map((row) => (
                                <Row gutter={16} key={row.key} align="middle">
                                    <Col xs={24} lg={10}>
                                        <Form.Item {...row} name={[row.name, 'method']} label="Метод">
                                            <Select
                                                allowClear
                                                showSearch
                                                optionFilterProp="label"
                                                placeholder={geoValue ? `Методы для гео «${geoValue}»` : 'Выберите метод'}
                                                options={allowed.map((item) => ({ value: item, label: item }))}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} lg={6}>
                                        <Form.Item {...row} name={[row.name, 'conditions_deposit']} label="Прием, %">
                                            <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} lg={6}>
                                        <Form.Item {...row} name={[row.name, 'conditions_withdrawal']} label="Вывод, %">
                                            <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} lg={2}>
                                        <Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(row.name)} />
                                    </Col>
                                </Row>
                            ))}
                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={() => add({ method: allowed[0] || null, conditions_deposit: null, conditions_withdrawal: null })}
                            >
                                Добавить метод
                            </Button>
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Список методов совпадает со справочником CRM Wiseway для гео лида.
                                    Нельзя ставить 0 одновременно в «Прием» и «Вывод» — это их правило.
                                </Text>
                            </div>
                        </>
                    )}
                </Form.List>
            </div>
        )
            }}
        </Form.Item>
    )

    const renderField = (field: LeadFieldConfig) => {
        const lockedAfterCreate = !createMode && LOCKED_AFTER_CREATE.has(String(field.name))
        const readOnly = READONLY_LEAD_FIELDS.has(String(field.name)) || lockedAfterCreate
        // Пометку ставим только там, где карточка действительно связана с их
        // базой: у лида, заведённого руками, связывать нечего.
        const linked = Boolean(selectedLead?.crm_ww_id)
        const syncedWithCrm = linked && (crmDict
            ? crmDict.synced_fields.includes(String(field.name))
            : CRM_SYNCED_FIELDS.has(String(field.name)))
        const pullOnly = linked && !syncedWithCrm && Boolean(crmDict?.readonly_fields.includes(String(field.name)))
        let control: ReactNode = <Input />
        if (field.type === 'textarea') {
            control = <TextArea rows={3} />
        } else if (field.type === 'date') {
            control = <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
        } else if (userPickFields.has(field.name)) {
            control = (
                <Select
                    showSearch
                    allowClear
                    mode="tags"
                    placeholder="Выберите пользователя или введите вручную"
                    options={operators.map((operator) => ({
                        value: operator.full_name || operator.username,
                        label: `${operator.full_name || operator.username}${operator.department ? ` · ${operator.department}` : ''}`,
                    }))}
                />
            )
        } else if (String(field.name) === 'source') {
            // Источник в их карточке - список из их же справочника.
            const known = crmDict?.sources || []
            const current = selectedLead?.source || ''
            control = (
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Выберите источник из справочника CRM"
                    options={[...known, ...(current && !known.includes(current) ? [current] : [])]
                        .map((item) => ({ value: item, label: item }))}
                />
            )
        } else if (crmDict?.options?.[String(field.name)] || CRM_OPTIONS[String(field.name)]) {
            const options = crmDict?.options?.[String(field.name)] || CRM_OPTIONS[String(field.name)]
            const current = selectedLead ? String(selectedLead[field.name] ?? '') : ''
            // Если в карточке уже лежит значение не из списка - показываем его
            // отдельным пунктом, иначе поле выглядело бы пустым.
            const extra = current && !options.includes(current) ? [current] : []
            control = (
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Выберите значение из списка CRM"
                    options={[...options, ...extra].map((item) => ({ value: item, label: item }))}
                />
            )
        } else if (field.name === 'geo') {
            control = <Select showSearch allowClear options={geos.map((geo) => ({ value: geo.name, label: geo.name }))} />
        } else if (field.name === 'temperature') {
            control = <Select options={TEMPERATURE_OPTIONS} />
        } else if (field.name === 'assigned_to_id') {
            control = (
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Выберите ответственного"
                    options={operators.map((operator) => ({
                        value: operator.id,
                        label: `${operator.full_name || operator.username}${operator.department ? ` · ${operator.department}` : ''}`,
                    }))}
                />
            )
        } else if (field.name === 'status') {
            control = (
                <Select
                    options={statuses.map((status) => ({ value: status.key, label: status.label }))}
                    onChange={(value) => {
                        const nextStatus = statuses.find((status) => status.key === value)
                        form.setFieldsValue({ sub_status: nextStatus?.substatuses?.[0] || null })
                    }}
                />
            )
        } else if (field.name === 'sub_status') {
            control = <Select allowClear options={(currentStatus?.substatuses || []).map((item) => ({ value: item, label: item }))} />
        }

        if (readOnly) control = <Input disabled />

        const wide = field.full || field.type === 'textarea'
        return (
            <Col xs={24} lg={wide ? 24 : 12} xl={wide ? 24 : 8} key={String(field.name)}>
                <Form.Item
                    name={field.name as string}
                    label={syncedWithCrm || pullOnly ? (
                        <Space size={4}>
                            {field.label}
                            <Tooltip title={syncedWithCrm
                                ? 'Поле связано с карточкой CRM Wiseway: изменение уходит туда, их правка приезжает сюда'
                                : 'Значение приезжает из CRM Wiseway, обратно не передаётся — менять его нужно в их карточке'}>
                                <span style={{ opacity: 0.45 }}>{syncedWithCrm ? '⇄' : '⇠'}</span>
                            </Tooltip>
                        </Space>
                    ) : field.label}
                    rules={field.required && !readOnly ? [{ required: true, message: 'Заполните поле' }] : undefined}
                    extra={lockedAfterCreate
                        ? 'Менять нельзя: по нему привязан чат и сверяется карточка CRM Wiseway'
                        : readOnly
                            ? 'Значение из CRM Wiseway, изменить нельзя'
                            : pullOnly ? 'Правится в карточке CRM Wiseway' : undefined}
                >
                    {control}
                </Form.Item>
            </Col>
        )
    }

    // Кнопка видна, только если менять есть что и есть на что: у лида есть
    // карточка в CRM, роли доступен хоть один статус, и лид свой либо роль
    // видит всех. Сервер проверяет то же самое.
    // Лид своей команды (профиля) - тоже свой, когда включена видимость по профилям.
    const inMyProfile = Boolean(
        user?.visibility_profiles_enabled
        && selectedLead?.visibility_profile_id
        && user?.visibility_profile_ids?.includes(selectedLead.visibility_profile_id)
    )
    const canChangeStatus = Boolean(
        selectedLead?.crm_ww_id
        && (user?.allowed_statuses?.length ?? 0) > 0
        && (seesAllLeads(user?.role) || selectedLead?.assigned_to_id === user?.id || inMyProfile)
    )
    // Профиль при заведении лида: с правом назначать - любой, иначе только свой.
    const createProfileOptions = profiles
        .filter((profile) => profile.is_active)
        .filter((profile) => user?.permissions?.includes('assign_lead') || user?.visibility_profile_ids?.includes(profile.id))
        .map((profile) => ({ value: profile.id, label: profileLabel(profile.name, profile.wiseway_email) }))
    const defaultCreateProfileId = user?.visibility_profile_ids?.find((id) => createProfileOptions.some((option) => option.value === id))

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 92,
            sorter: (left: Lead, right: Lead) => left.id - right.id,
            render: (id: number) => <Tag color="geekblue">#{id}</Tag>,
        },
        {
            title: 'Лид',
            key: 'lead',
            // Ширина задана явно: колонок стало больше, и без неё имя лида
            // сжималось до одной буквы в строке на узких экранах.
            width: 260,
            sorter: (left: Lead, right: Lead) => compareText(leadName(left), leadName(right)),
            render: (_: unknown, lead: Lead) => (
                <Space size={10}>
                    <Avatar src={lead.telegram_avatar_url || undefined} icon={<UserOutlined />} />
                    <Space direction="vertical" size={0}>
                        <Text strong>{leadName(lead)}</Text>
                        <Text type="secondary">{leadContact(lead)}</Text>
                    </Space>
                </Space>
            ),
        },
        {
            title: 'ГЕО',
            dataIndex: 'geo',
            width: 150,
            sorter: (left: Lead, right: Lead) => compareText(left.geo, right.geo),
            render: (value: string | null) => value || '-',
        },
        {
            title: 'Температура',
            key: 'temperature',
            width: 150,
            sorter: (left: Lead, right: Lead) => temperatureRank(left.temperature) - temperatureRank(right.temperature),
            render: (_: unknown, lead: Lead) => lead.temperature ? (
                <Tag color={temperatureColor(lead.temperature, 'default')}>{temperatureLabel(lead.temperature)}</Tag>
            ) : '-',
        },
        // СТАТУСНАЯ МОДЕЛЬ ОТКЛЮЧЕНА: колонка статуса скрыта из таблицы лидов.
        // {
        //     title: 'Статус',
        //     key: 'status',
        //     width: 260,
        //     render: (_: unknown, lead: Lead) => {
        //         const status = statusMap.get(lead.status)
        //         return (
        //             <Space direction="vertical" size={2}>
        //                 <Tag color="blue">{status?.label || lead.status}</Tag>
        //                 {lead.sub_status && <Text type="secondary" style={{ fontSize: 12 }}>{lead.sub_status}</Text>}
        //             </Space>
        //         )
        //     },
        // },
        {
            title: 'Статус CRM',
            key: 'crm_status',
            width: 200,
            sorter: (left: Lead, right: Lead) => compareText(left.crm_status_label, right.crm_status_label),
            render: (_: unknown, lead: Lead) => lead.crm_status_label
                ? <Tag color="purple">{lead.crm_status_label}</Tag>
                : <Text type="secondary">не прочитан</Text>,
        },
        {
            title: 'Ответственный',
            key: 'assigned',
            width: 200,
            // Сортировка по имени собирает лидов одного сотрудника подряд -
            // так видно, кто чем занят, не переключая фильтр.
            sorter: (left: Lead, right: Lead) =>
                compareText(
                    left.visibility_profile_name || left.assigned_to_name || left.manager,
                    right.visibility_profile_name || right.assigned_to_name || right.manager,
                ),
            // Ответственный - профиль (команда). Под ним - кто из команды сейчас
            // ведёт лида. Лид без профиля показывает сотрудника, как раньше.
            render: (_: unknown, lead: Lead) => (
                <Space direction="vertical" size={0}>
                    {lead.visibility_profile_name
                        || lead.assigned_to_name
                        || lead.manager
                        || <Text type="secondary">без ответственного</Text>}
                    {lead.visibility_profile_name && lead.assigned_to_name && (
                        <Text type="secondary" style={{ fontSize: 12 }}>ведёт: {lead.assigned_to_name}</Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Обновлен',
            dataIndex: 'updated_at',
            width: 160,
            sorter: (left: Lead, right: Lead) => dayjs(left.updated_at).valueOf() - dayjs(right.updated_at).valueOf(),
            render: (value: string) => dayjs(value).format('DD.MM.YYYY HH:mm'),
        },
        {
            title: '',
            key: 'actions',
            width: 112,
            render: (_: unknown, lead: Lead) => (
                <Space size={4}>
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={(event) => {
                            event.stopPropagation()
                            navigate(`/contacts/${lead.id}`)
                        }}
                    />
                    {user?.can_delete_leads !== false && (
                        <Popconfirm
                            title="Удалить лида?"
                            description="Лид, его чат и переписка будут удалены."
                            okText="Удалить"
                            cancelText="Отмена"
                            onConfirm={() => handleDeleteLead(lead)}
                        >
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={(event) => event.stopPropagation()}
                            />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ]

    return (
        <div className={embedded ? undefined : 'lead-workspace'} style={{ paddingTop: embedded ? 0 : 22 }}>
            {!embedded && (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center" wrap>
                    <div>
                        <Title level={3} style={{ margin: 0 }}>Лиды</Title>
                        <Text type="secondary">Добавление, импорт и карточки лидов</Text>
                    </div>
                    <Space wrap>
                        <Input.Search
                            allowClear
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onSearch={loadLeads}
                            placeholder={user?.contacts_hidden ? "Поиск по лиду и ID CRM WW" : "Поиск по лиду, TG, email, телефону"}
                            style={{ width: 320 }}
                        />
                        <Button icon={<ReloadOutlined />} onClick={loadLeads} loading={loading} />
                        {!user?.block_manual_lead_create && (
                            <>
                                <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>Импорт</Button>
                                {/* Режим «только по ID»: та же кнопка открывает окно поиска
                                    карточки, а не пустую форму. */}
                                <Button
                                    type="primary"
                                    icon={user?.crm_id_only_lead_create ? <SearchOutlined /> : <PlusOutlined />}
                                    onClick={user?.crm_id_only_lead_create ? openCrmIdSearch : openCreate}
                                >
                                    Добавить лида
                                </Button>
                            </>
                        )}
                    </Space>
                </Space>

                {/* Оба фильтра и действия над выбранными - одной строкой.
                    Фильтр по статусу доступен всем, кто видит раздел; остальное
                    касается чужих лидов и остаётся за руководителями. */}
                <Space wrap style={{ width: '100%' }} size={12}>
                    <Select
                        showSearch
                        optionFilterProp="label"
                        value={statusFilter}
                        onChange={setStatusFilter}
                        style={{ width: 260 }}
                        options={statusOptions}
                    />
                    {isManager && (
                        <Select
                            value={assigneeFilter}
                            onChange={setAssigneeFilter}
                            style={{ width: 260 }}
                            showSearch
                            optionFilterProp="label"
                            options={[
                                { value: 'all', label: `Все ответственные (${leads.length})` },
                                { value: 'none', label: 'Без ответственного' },
                                ...(profiles.length ? [{
                                    label: 'Профили',
                                    options: profiles.map((profile) => ({
                                        value: `p:${profile.id}`,
                                        label: profile.name,
                                    })),
                                }] : []),
                                {
                                    label: 'Сотрудники (кто ведёт)',
                                    options: operators.map((operator) => ({
                                        value: `o:${operator.id}`,
                                        label: operator.full_name || operator.username,
                                    })),
                                },
                            ]}
                        />
                    )}
                    {statusFilter !== 'all' && (
                        <Button type="text" onClick={() => setStatusFilter('all')}>Сбросить статус</Button>
                    )}
                    {canBulkStatus && (
                        <Button
                            icon={<SwapOutlined />}
                            disabled={!selectedIds.length}
                            onClick={openBulkStatus}
                        >
                            Сменить статус{selectedIds.length ? ` (${selectedIds.length})` : ''}
                        </Button>
                    )}
                    {isManager && (
                        <>
                            <Button
                                icon={<TeamOutlined />}
                                type={summaryOpen ? 'primary' : 'default'}
                                onClick={() => setSummaryOpen((open) => !open)}
                            >
                                Загрузка по сотрудникам
                            </Button>
                            <Button
                                type="primary"
                                icon={<SwapOutlined />}
                                disabled={!selectedIds.length}
                                onClick={openTransfer}
                            >
                                Передать выбранных{selectedIds.length ? ` (${selectedIds.length})` : ''}
                            </Button>
                        </>
                    )}
                    {selectedIds.length > 0 && (
                        <Button type="text" onClick={() => setSelectedIds([])}>Снять отметки</Button>
                    )}
                </Space>

                {isManager && summaryOpen && (
                    <Table
                        rowKey={(row) => String(row.operator_id ?? 'none')}
                        size="small"
                        pagination={false}
                        dataSource={summary}
                        columns={[
                            {
                                title: 'Сотрудник',
                                key: 'name',
                                render: (_: unknown, row: AssigneeSummaryRow) => (
                                    <Space size={8}>
                                        <Text strong={row.lead_count > 0}>{row.name}</Text>
                                        {!row.is_active && <Tag>отключён</Tag>}
                                    </Space>
                                ),
                            },
                            {
                                title: 'Всего лидов',
                                dataIndex: 'lead_count',
                                width: 140,
                                sorter: (a: AssigneeSummaryRow, b: AssigneeSummaryRow) => a.lead_count - b.lead_count,
                                render: (value: number) => <Tag color={value ? 'geekblue' : 'default'}>{value}</Tag>,
                            },
                            {
                                title: 'Из них в работе',
                                dataIndex: 'active_count',
                                width: 160,
                                sorter: (a: AssigneeSummaryRow, b: AssigneeSummaryRow) => a.active_count - b.active_count,
                                render: (value: number) => <Tag color={value ? 'green' : 'default'}>{value}</Tag>,
                            },
                            {
                                title: '',
                                key: 'filter',
                                width: 140,
                                render: (_: unknown, row: AssigneeSummaryRow) => (
                                    <Button
                                        type="link"
                                        size="small"
                                        onClick={() => setAssigneeFilter(row.operator_id != null ? `o:${row.operator_id}` : 'none')}
                                    >
                                        Показать лидов
                                    </Button>
                                ),
                            },
                        ]}
                    />
                )}

                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={visibleLeads}
                    rowClassName={(lead) => (lead.crm_fraud ? 'lead-row-fraud' : '')}
                    loading={loading}
                    rowSelection={(isManager || canBulkStatus) ? {
                        selectedRowKeys: selectedIds,
                        onChange: (keys) => setSelectedIds(keys as number[]),
                        // «Выбрать все» отмечает всё, что попало под фильтр, а не
                        // только текущую страницу таблицы.
                        selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
                    } : undefined}
                    // Колонок много: на узких экранах таблица прокручивается по
                    // горизонтали, а не сплющивает содержимое до одной буквы в строке.
                    scroll={{ x: 1240 }}
                    pagination={{
                        pageSize: leadsPageSize,
                        showSizeChanger: true,
                        pageSizeOptions: LEADS_PAGE_SIZES,
                        showTotal: (total) => `${total} лидов`,
                        onChange: (_page, size) => {
                            if (!size || size === leadsPageSize) return
                            setLeadsPageSize(size)
                            try {
                                localStorage.setItem(LEADS_PAGE_SIZE_KEY, String(size))
                            } catch {
                                // без сохранения: размер останется до перезагрузки страницы
                            }
                        },
                    }}
                    onRow={(lead) => ({
                        onClick: (event: React.MouseEvent) => {
                            // Клик по колонке отметок не должен открывать карточку.
                            if ((event.target as HTMLElement).closest('.ant-table-selection-column')) return
                            navigate(`/contacts/${lead.id}`)
                        },
                        // Правая кнопка - управление лидом, без открытия карточки.
                        onContextMenu: (event: React.MouseEvent) => {
                            if (!isManager) return
                            event.preventDefault()
                            setMenuLead(lead)
                            setMenuPos({ x: event.clientX, y: event.clientY })
                        },
                        style: { cursor: 'pointer' },
                    })}
                />
            </Space>
            )}

            {isManager && menuLead && menuPos && (
                <Dropdown
                    open
                    trigger={['contextMenu']}
                    onOpenChange={(open) => {
                        if (!open && !assignOpen && !moveAction) {
                            setMenuPos(null)
                            setMenuLead(null)
                        }
                    }}
                    menu={{
                        items: [
                            { key: 'assign', label: 'Назначить ответственного…' },
                            { type: 'divider' as const },
                            ...WORK_STATES.map((item) => ({
                                key: item.key,
                                label: `Перенести в «${item.label}»`,
                                disabled: (menuLead.work_state || 'none') === item.state,
                            })),
                        ],
                        onClick: ({ key }) => {
                            setMenuPos(null)
                            if (key === 'assign') {
                                setAssignValue(menuLead.assigned_to_id ?? undefined)
                                setAssignOpen(true)
                                return
                            }
                            startMove(menuLead, key as WorkAction)
                        },
                    }}
                >
                    <div style={{ position: 'fixed', left: menuPos.x, top: menuPos.y, width: 1, height: 1 }} />
                </Dropdown>
            )}

            <Modal
                title={`Сменить статус: ${selectedIds.length} лид(ов)`}
                open={bulkStatusOpen}
                onCancel={() => setBulkStatusOpen(false)}
                onOk={submitBulkStatus}
                okText="Сменить статус"
                cancelText="Отмена"
                confirmLoading={bulkStatusSaving}
            >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Select
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                        placeholder="Выберите статус"
                        loading={bulkStatusLoading}
                        value={bulkStatusValue}
                        onChange={setBulkStatusValue}
                        options={bulkStatusGroups.map((group) => ({
                            label: group.group,
                            options: group.reasons.map((reason) => ({ value: reason.code, label: reason.label })),
                        }))}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Статус проставится каждому отмеченному лиду и уйдёт в CRM Wiseway —
                        так же, как при смене статуса в одной карточке. В списке только те
                        статусы, что доступны вашей роли. Лиды без карточки CRM и чужие
                        будут пропущены, остальные обработаются.
                    </Text>
                </Space>
            </Modal>

            <Modal
                title={`Передать лидов: ${selectedIds.length}`}
                open={transferOpen}
                onCancel={() => setTransferOpen(false)}
                onOk={submitTransfer}
                okText="Передать"
                cancelText="Отмена"
                confirmLoading={transferring}
            >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Select
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                        placeholder="Выберите сотрудника"
                        value={transferTo}
                        onChange={setTransferTo}
                        options={[
                            { value: null, label: 'Снять ответственного (вернуть в общий пул)' },
                            ...operators.map((operator) => ({
                                value: operator.id,
                                label: operator.full_name || operator.username,
                            })),
                        ]}
                    />
                    <Input.TextArea
                        rows={2}
                        maxLength={500}
                        value={transferComment}
                        onChange={(event) => setTransferComment(event.target.value)}
                        placeholder="Причина передачи (необязательно) — попадёт в историю карточки"
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Меняется только ответственный: статус лида и переписка остаются как были.
                        Диалог переезжает к новому сотруднику вместе с карточкой. Каждая передача
                        записывается в историю карточки — кто, когда и с кого на кого.
                        В CRM Wiseway при этом ничего не пишется.
                    </Text>
                </Space>
            </Modal>

            <Modal
                title={menuLead ? `Ответственный по лиду #${menuLead.id}` : 'Ответственный'}
                open={assignOpen}
                onCancel={() => { setAssignOpen(false); setMenuLead(null) }}
                onOk={submitAssign}
                okText="Назначить"
                cancelText="Отмена"
                confirmLoading={workflowSaving}
            >
                <Select
                    showSearch
                    optionFilterProp="label"
                    style={{ width: '100%' }}
                    placeholder="Выберите сотрудника"
                    value={assignValue}
                    onChange={setAssignValue}
                    options={operators.map((operator) => ({
                        value: operator.id,
                        label: operator.full_name || operator.username,
                    }))}
                />
            </Modal>

            <Modal
                title={moveAction ? `Перенести в «${WORK_STATES.find((item) => item.key === moveAction)?.label}»` : ''}
                open={Boolean(moveAction)}
                onCancel={() => { closeWorkflowModal(); setMenuLead(null) }}
                onOk={submitMove}
                okText="Перенести"
                cancelText="Отмена"
                confirmLoading={workflowSaving}
            >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {(needsCloseReason || needsReopenReason) && (
                        <Select
                            showSearch
                            optionFilterProp="label"
                            style={{ width: '100%' }}
                            placeholder={needsCloseReason ? 'Причина закрытия' : 'Статус, в который вернуть лида'}
                            value={moveOutcome}
                            onChange={setMoveOutcome}
                            options={(needsCloseReason ? closeGroups : reopenGroups)
                                .flatMap((group) => group.reasons)
                                .map((reason) => ({ value: reason.code, label: reason.label }))}
                        />
                    )}
                    <Input.TextArea
                        rows={3}
                        value={moveComment}
                        onChange={(event) => setMoveComment(event.target.value)}
                        placeholder={needsComment ? 'Комментарий (обязательно)' : 'Комментарий (необязательно)'}
                    />
                    {needsCloseReason && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Причина уходит в CRM Wiseway, если передача статусов включена в настройках.
                        </Text>
                    )}
                </Space>
            </Modal>

            <Drawer
                width={embedded ? embeddedWidth : 980}
                title={createMode ? 'Новый лид' : selectedLead ? `Лид #${selectedLead.id}` : 'Лид'}
                open={createMode || Boolean(leadId)}
                onClose={closeDrawer}
                destroyOnClose={false}
                // Во встроенном режиме карточка не перекрывает чат и не гасит его
                // затемнением: из переписки копируют данные прямо в поля.
                mask={!embedded}
                keyboard={!embedded}
                footer={
                    <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                        <div>{selectedLead && <Tag color="geekblue">ID агента в ссылке: #{selectedLead.id}</Tag>}</div>
                        <Space wrap>
                            {!createMode && selectedLead && user?.can_delete_leads !== false && (
                                <Popconfirm
                                    title="Удалить лида?"
                                    description="Лид, его чат и переписка будут удалены."
                                    okText="Удалить"
                                    cancelText="Отмена"
                                    onConfirm={() => handleDeleteLead(selectedLead)}
                                >
                                    <Button icon={<DeleteOutlined />}>Удалить лида</Button>
                                </Popconfirm>
                            )}
                            {!createMode && selectedLead?.chat_id && (
                                <Button icon={<MessageOutlined />} onClick={() => openLeadChat(selectedLead)}>
                                    Перейти в чат
                                </Button>
                            )}
                            <Button onClick={closeDrawer}>Закрыть</Button>
                            {createMode && (
                                <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>Сохранить</Button>
                            )}
                        </Space>
                    </Space>
                }
            >
                <Spin spinning={detailsLoading}>
                    <Tabs
                        items={[
                            {
                                key: 'card',
                                label: 'Карточка',
                                children: (!createMode && selectedLead) ? (
                                    <LeadCard
                                        lead={selectedLead}
                                        geos={geos.map((geo) => geo.name)}
                                        crmDict={crmDict}
                                        operators={operators}
                                        canEdit={user?.can_edit_leads !== false}
                                        canChangeStatus={canChangeStatus}
                                        onLeadChange={(lead) => {
                                            setSelectedLead(lead)
                                            setLeads((items) => items.map((item) => item.id === lead.id ? lead : item))
                                        }}
                                        onReload={async () => { await Promise.all([loadLeadDetails(selectedLead.id), loadLeads()]) }}
                                        onOpenChat={embedded ? undefined : () => openLeadChat(selectedLead)}
                                    />
                                ) : (
                                    <Form form={form} layout="vertical" disabled={!createMode && !editMode}>
                                        {createMode && createProfileOptions.length > 0 && (
                                            <Form.Item
                                                name="visibility_profile_id"
                                                label="Профиль видимости"
                                                initialValue={defaultCreateProfileId}
                                                extra="Команда, которой будет принадлежать лид: его увидят и смогут вести её сотрудники."
                                            >
                                                <Select options={createProfileOptions} allowClear placeholder="Без профиля" />
                                            </Form.Item>
                                        )}
                                        {visibleSections.map((section) => (
                                            <div key={section.title} style={{ marginBottom: 22 }}>
                                                <Title level={5} style={{ marginBottom: 14 }}>{section.title}</Title>
                                                <Row gutter={16}>{section.fields.map(renderField)}</Row>
                                            </div>
                                        ))}
                                        {renderMethodsEditor()}
                                    </Form>
                                ),
                            },
                            ...(!createMode && selectedLead ? [{
                                key: 'tasks',
                                label: (
                                    <span>
                                        Задачи
                                        {openTaskCount > 0 && (
                                            <span className={`lead-tasks-count${overdueTaskCount ? ' is-overdue' : ''}`}>{openTaskCount}</span>
                                        )}
                                    </span>
                                ),
                                children: (
                                    <LeadTasksTab
                                        lead={{ id: selectedLead.id, label: selectedLead.title || `Лид #${selectedLead.id}`, geo: selectedLead.geo, crm_ww_id: selectedLead.crm_ww_id }}
                                        tasks={leadTasks}
                                        loading={leadTasksLoading}
                                        onReload={() => loadLeadTasks(selectedLead.id)}
                                    />
                                ),
                            }] : []),
                            {
                                key: 'activity',
                                label: 'История и активность',
                                children: visibleActivities.length ? (
                                    <Timeline
                                        items={visibleActivities.map((activity) => ({
                                            children: (
                                                <Space direction="vertical" size={2}>
                                                    <Text>{activityText(activity, statusLabelByKey)}</Text>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                        {dayjs(activity.created_at).format('DD.MM.YYYY HH:mm')} · {activity.operator_name || 'Система'}
                                                        {activity.operator_email ? ` · ${activity.operator_email}` : ''}
                                                    </Text>
                                                </Space>
                                            ),
                                        }))}
                                    />
                                ) : (
                                    <Empty description="История пока пустая" />
                                ),
                            },
                        ]}
                    />
                </Spin>
            </Drawer>

            <Modal
                title="Импорт лидов"
                open={importOpen}
                onCancel={() => {
                    setImportOpen(false)
                    setImportFile(null)
                }}
                onOk={handleImport}
                okText="Импортировать"
                confirmLoading={importing}
            >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Text type="secondary">
                        Поддерживаются Excel, CSV, TSV и TXT. Для Excel/CSV/TSV первая строка должна содержать названия колонок, порядок колонок не важен.
                    </Text>
                    <div className="lead-import-help">
                        <Text strong>Минимально:</Text>
                        <Text code>Название лида, ГЕО, TG Nickname, Телефон, Источник</Text>
                        <Text type="secondary">Антидубль проверяет телефон и TG username. TXT читается построчно как название лида.</Text>
                    </div>
                    <Upload.Dragger {...uploadProps}>
                        <p className="ant-upload-drag-icon"><UploadOutlined /></p>
                        <p className="ant-upload-text">Выберите файл или перетащите его сюда</p>
                    </Upload.Dragger>
                </Space>
            </Modal>

            <Modal
                title="Новый лид по ID CRM WW"
                open={crmIdOpen}
                onCancel={() => setCrmIdOpen(false)}
                width={620}
                footer={[
                    <Button key="cancel" onClick={() => setCrmIdOpen(false)}>Отмена</Button>,
                    <Button
                        key="add"
                        type="primary"
                        icon={<PlusOutlined />}
                        disabled={!crmCard?.can_add}
                        loading={crmSaving}
                        onClick={confirmCrmCard}
                    >
                        {crmCard?.duplicate ? 'Всё равно добавить' : 'Добавить лида'}
                    </Button>,
                ]}
            >
                <Space direction="vertical" size={14} style={{ width: '100%' }}>
                    <Text type="secondary">
                        Заполнять руками ничего не нужно: найдите карточку по её номеру в CRM Wiseway,
                        проверьте данные и решите, заводить лида или нет.
                    </Text>

                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            autoFocus
                            value={crmIdValue}
                            onChange={(event) => { setCrmIdValue(event.target.value); setCrmCard(null); setCrmError(null) }}
                            onPressEnter={searchCrmCard}
                            placeholder="ID CRM WW, например 10254"
                        />
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            loading={crmSearching}
                            disabled={!crmIdValue.trim()}
                            onClick={searchCrmCard}
                        >
                            Найти
                        </Button>
                    </Space.Compact>

                    {crmError && <Alert type="error" showIcon message={crmError} />}

                    {crmCard && (
                        <Space direction="vertical" size={10} style={{ width: '100%' }}>
                            <div className="lead-import-help">
                                <Text strong style={{ fontSize: 15 }}>{crmCard.title}</Text>
                                <Text type="secondary">ID CRM WW {crmCard.crm_ww_id}</Text>
                            </div>

                            <Descriptions column={2} size="small" bordered
                                items={[
                                    { key: 'source', label: 'Источник', children: crmCard.source || '—' },
                                    { key: 'geo', label: 'ГЕО', children: crmCard.geo || '—' },
                                    {
                                        key: 'temperature',
                                        label: 'Пул',
                                        children: (
                                            <Tag color={temperatureColor(crmCard.temperature)}>
                                                {temperatureLabel(crmCard.temperature)}
                                            </Tag>
                                        ),
                                    },
                                    { key: 'status', label: 'Статус в CRM', children: crmCard.crm_status || '—' },
                                    {
                                        key: 'telegram',
                                        label: 'Телеграм',
                                        children: crmCard.contacts_hidden
                                            ? <Text type="secondary">{crmCard.has_telegram ? 'скрыт' : 'не заполнен'}</Text>
                                            : (crmCard.telegram || '—'),
                                    },
                                    {
                                        key: 'phone',
                                        label: 'Телефон',
                                        children: crmCard.contacts_hidden
                                            ? <Text type="secondary">скрыт</Text>
                                            : (crmCard.phone || '—'),
                                    },
                                    {
                                        key: 'assignee',
                                        label: 'Ответственный',
                                        children: crmCard.assignee_name || <Text type="secondary">не найден у нас</Text>,
                                        span: 2,
                                    },
                                    {
                                        key: 'profile',
                                        label: 'Профиль видимости',
                                        children: crmCard.profile_name || <Text type="secondary">не найден по почте ответственного</Text>,
                                        span: 2,
                                    },
                                ]}
                            />

                            {crmCard.blocker && <Alert type="error" showIcon message={crmCard.blocker} />}

                            {crmCard.duplicate && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message="Похоже на дубль"
                                    description={
                                        <>
                                            Такой человек уже заведён: лид #{crmCard.duplicate.lead_id}{' '}
                                            «{crmCard.duplicate.title}» — совпадает телеграм или телефон.
                                            Кнопка «Всё равно добавить» заведёт вторую карточку на того же человека.
                                        </>
                                    }
                                />
                            )}

                            {crmCard.can_add && !crmCard.duplicate && (
                                <Alert type="success" showIcon message="Дублей не найдено, лида можно добавить" />
                            )}
                        </Space>
                    )}
                </Space>
            </Modal>
        </div>
    )
}
